import { Button, Grid, Paper, Popover, Typography } from "@material-ui/core";
import clsx from "clsx";
import React, { useState } from "react";
import { batchRowStyles } from "./BatchRowStyles";
import { observer } from "mobx-react";
import BatchDetails from "../BatchDetails/BatchDetails";
import { useStore } from "store/StoreConfigs";
import { ReactComponent as GreenCheckMark } from "icons/GreenCheckMark.svg";
import { ReactComponent as DangerIcon } from "icons/DangerIcon.svg";
import { ReactComponent as YellowDangerIcon } from "icons/YellowDangerIcon.svg";
import { AssessmentStatus } from "models/thorEventModels/BatchImpactAssessment";
import { ReactComponent as ReviewerQA } from "icons/ReviewerQA.svg";
import { ReactComponent as EditButton } from "icons/EditButton.svg";
import { ReactComponent as QuantitySubmit } from "icons/QuantitySubmit.svg";
import { EventStatus } from "models/thorEventModels/EventDetails";
import { BatchRequest } from "api/thorRequestModels/Events/BatchRequest";
import { EventRequest } from "api/thorRequestModels/Events/EventRequest";
import { BatchRowProps } from "./BatchRowProps";
import { AssignAssessmentRequest } from "api/thorRequestModels/Events/AssignAssessmentRequest";

export default observer(function BatchRow({ batch }: BatchRowProps) {
    const classes = batchRowStyles();
    const { thorEventViewStore, identityStore } = useStore();

    const isUserHavePermission: boolean =
        identityStore.userInfo?.email ===
        (thorEventViewStore.eventDetails.reviewerUserId || thorEventViewStore.eventDetails.qaUserId);

    const collapseOpen = () => {
        if (batch.batchId === thorEventViewStore.selectedBatch) {
            thorEventViewStore.setSelectedBatch(0);
        } else {
            if (
                thorEventViewStore.eventDetails.eventStatusId === EventStatus.ReOpened &&
                batch.assessmentStatusId !== AssessmentStatus.ReAssessing
            ) {
                const request: AssignAssessmentRequest = {
                    batchId: batch.batchId,
                    eventId: thorEventViewStore.eventDetails.eventId,
                    customerId: identityStore.currentCustomerId,
                };

                thorEventViewStore.setAssessmentStatus(request);
            }

            thorEventViewStore.setSelectedBatch(batch.batchId);

            const request: BatchRequest = {
                batchId: batch.batchId,
                customerId: identityStore.currentCustomerId,
            };
            thorEventViewStore.getProductFlows(request);

            const deviceDataRequest: EventRequest = {
                customerId: identityStore.currentCustomerId,
                eventId: thorEventViewStore.eventDetails.eventId,
            };
            thorEventViewStore.loadDeviceData(deviceDataRequest);
        }
    };

    const [value, setValue] = useState<number | undefined>(batch.quantity);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setValue(undefined);
        setAnchorEl(event.currentTarget);
    };

    const handleChange = () => {
        if (value) {
            thorEventViewStore.setProductQuantity({
                transportationUnitId: batch.transportationUnitId,
                quantity: value,
                eventId: thorEventViewStore.eventDetails.eventId,
                customerId: identityStore.currentCustomerId,
            });
        }
        setAnchorEl(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
        <>
            <Paper
                elevation={0}
                className={clsx(classes.container, {
                    [classes.selected]: thorEventViewStore.selectedBatch === batch.batchId,
                })}
            >
                <Grid container justifyContent="space-between" direction="row">
                    <Grid item>
                        <Typography className={classes.icon}>
                            {batch.assessmentStatusId === AssessmentStatus.PendingReview ? (
                                <DangerIcon />
                            ) : batch.assessmentStatusId === AssessmentStatus.PendingDecision ? (
                                <YellowDangerIcon />
                            ) : batch.assessmentStatusId === AssessmentStatus.EvaluationCompleted ? (
                                <GreenCheckMark />
                            ) : (
                                <YellowDangerIcon />
                            )}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography className={classes.gridItem}>
                            {batch.assessmentStatusId === AssessmentStatus.PendingReview ? (
                                "Pending Review"
                            ) : batch.assessmentStatusId === AssessmentStatus.PendingDecision ? (
                                "Pending Decision"
                            ) : batch.assessmentStatusId === AssessmentStatus.EvaluationCompleted ? (
                                // TODO: redo
                                <ReviewerQA style={{ position: "absolute", marginTop: "-5px", right: "91%" }} />
                            ) : (
                                "Re-Assessing"
                            )}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography className={classes.gridItem}>
                            Product: <span className={classes.highlightedText}>{batch.productName}</span>
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography className={classes.gridItem}>
                            Batch: <span className={classes.sku}>#{batch.batchNumber}</span>
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Typography className={classes.gridItem}>
                            Quantity:{" "}
                            <>
                                <span className={classes.highlightedText}>
                                    {batch.quantity}
                                    {isUserHavePermission && batch.details.decision === "Pending" && (
                                        <span onClick={handleClick}>
                                            <EditButton className={classes.quantityEditButton} />
                                        </span>
                                    )}
                                </span>
                                <Popover
                                    id={id}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: "center",
                                        horizontal: "center",
                                    }}
                                    transformOrigin={{
                                        vertical: "center",
                                        horizontal: "left",
                                    }}
                                    className={classes.quantityPopup}
                                >
                                    <Paper className={classes.quantityMainPaper}>
                                        <input
                                            autoFocus
                                            onChange={(e) => {
                                                setValue(parseInt(e.target.value));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleChange();
                                                }
                                            }}
                                            className={classes.quantityInput}
                                            type="number"
                                            value={value}
                                        ></input>
                                        <QuantitySubmit
                                            className={classes.quantitySubmitButton}
                                            onClick={handleChange}
                                        />
                                    </Paper>
                                </Popover>
                            </>
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Typography className={classes.gridItem}>
                            Decision:{" "}
                            <span className={classes.highlightedText}>
                                {batch.details.decision === "Pending"
                                    ? "Pending"
                                    : batch.details.decision === "Approved"
                                    ? "Excursion Supported"
                                    : "Excursion Not Supported"}
                            </span>
                        </Typography>
                    </Grid>

                    <Grid item xs={3}>
                        <Button
                            className={clsx(classes.gridItem, classes.btn, {
                                [classes.reviewBtn]:
                                    thorEventViewStore.selectedBatch !== batch.batchId &&
                                    batch.details.decision !== "Pending",
                                [classes.minimizeBtn]:
                                    thorEventViewStore.selectedBatch === batch.batchId &&
                                    batch.details.decision !== "Pending",
                                [classes.notReviewedBtn]: batch.details.decision === "Pending",
                            })}
                            onClick={collapseOpen}
                        >
                            {thorEventViewStore.eventDetails.eventStatusId === EventStatus.ReOpened &&
                            batch.assessmentStatusId !== AssessmentStatus.ReAssessing
                                ? thorEventViewStore.selectedBatch === batch.batchId
                                    ? "Collapse"
                                    : "Edit"
                                : thorEventViewStore.selectedBatch === batch.batchId
                                ? "Collapse"
                                : "Review"}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            {thorEventViewStore.selectedBatch !== 0 && (
                <BatchDetails isCollapseOpen={thorEventViewStore.selectedBatch === batch.batchId} batch={batch} />
            )}
        </>
    );
});
